import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ProductRoutes } from '@core/constants/routes.const';
import { LocalStorageKey } from '@core/enums/local-storage-key.enum';
import { EcAppSettings } from '@core/models/app-settings.model';
import { RelatedCarouselSlide } from '@core/models/related-carousel-slide.model';
import { AppInitService } from '@core/services/app-init.service';
import { CartService } from '@core/services/cart.service';
import { LocalStorageService } from '@core/services/local-storage.service';
import { AppState } from '@core/store';
import { selectCartWithLimits, selectIsCartEmpty, selectLoading } from '@core/store/cart';
import { Cart, OverLimitItem } from '@core/store/cart/cart-state-models';
import {
  fetchCart,
  fetchCartSuccess,
  fetchProductsRecommendations,
  patchOrderLineLimits,
  setMultiplePurchaseLimitModalOpen,
  setOrderTotalLimitError,
  updateOrderLineQtySuccess,
} from '@core/store/cart/cart.actions';
import { purchaseLimitValidation } from '@core/store/checkout/checkout.actions';
import { selectCurrentConsultant } from '@core/store/consultant';
import { ProductRecommendation } from '@core/store/product/product-state-models';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { ProductUtilService } from '@shared/utils/product-util.service';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { CartItemUpdatedWarningModalComponent } from '../cart-item-updated-warning-modal/cart-item-updated-warning-modal.component';

@Component({
  selector: 'app-cart-page',
  templateUrl: './cart-page.component.html',
  styleUrls: ['./cart-page.component.scss'],
})
export class CartPageComponent implements OnInit, OnDestroy, AfterViewInit {
  productsRoute = ProductRoutes.Products;
  showCouponTextField: boolean = false;
  cart$: Observable<Cart>;
  recommendations$: Observable<ProductRecommendation[]>;
  slides$: Observable<RelatedCarouselSlide[]>;
  loading$: Observable<boolean>;
  appSettings: EcAppSettings;
  mxWholeSale$: Observable<boolean>;
  isCartEmpty$: Observable<boolean>;

  isOverOrderTotalLimit$: Observable<boolean>;
  hasOverLimitItems$: Observable<boolean>;
  isRevalidating = false;
  updatedItems: OverLimitItem[] = [];
  private subscriptions: Subscription = new Subscription();

  @ViewChild('itemUpdatedModal') private itemUpdatedModal: CartItemUpdatedWarningModalComponent;
  private hasOpenedModal: boolean = false;

  constructor(
    private store$: Store<AppState>,
    private productUtil: ProductUtilService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private cartService: CartService,
    private actions$: Actions,
    appInitService: AppInitService,
  ) {
    this.appSettings = appInitService.Settings.ec;
    this.cart$ = this.store$.select(selectCartWithLimits).pipe(filter((x) => !!x));
  }

  showCouponField(): void {
    this.showCouponTextField = true;
  }

  checkout(): void {
    const limit = this.appSettings.ecomOrderTotalLimit;
    let currentCart: Cart;

    if (limit > 0 && currentCart?.merchandiseTotal > limit) {
      this.store$.dispatch(
        setOrderTotalLimitError({
          payload: {
            limit: limit.toString(),
            orderLineId: null,
            errorType: 'orderTotalLimit',
          },
        }),
      );
      return;
    }

    this.store$.dispatch(purchaseLimitValidation());
  }

  ngOnInit(): void {
    this.store$.dispatch(fetchCart());

    this.subscriptions.add(
      this.actions$
        .pipe(
          ofType(fetchCartSuccess),
          take(1),
          tap(() => (this.isRevalidating = true)),
          switchMap(() => this.cartService.validateQuantityLimits()),
        )
        .subscribe({
          next: (result) => {
            if (result.allLimits?.length > 0) {
              this.store$.dispatch(patchOrderLineLimits({ limits: result.allLimits }));
            }

            this.isRevalidating = false;
          },
          error: () => {
            this.isRevalidating = false;
          },
        }),
    );

    this.recommendations$ = this.cart$.pipe(select((x) => x.recommendations || []));
    this.loading$ = this.store$.select(selectLoading);
    this.isCartEmpty$ = this.store$.select(selectIsCartEmpty);

    this.slides$ = this.recommendations$.pipe(
      filter((r) => !!r && !!r.length),
      map((r) => this.productUtil.getRelatedSlidesFromRecommendations(r)),
    );

    this.subscriptions.add(
      this.cart$
        .pipe(
          distinctUntilChanged(
            (a, b) => JSON.stringify(a.orderLines) === JSON.stringify(b.orderLines),
          ),
        )
        .subscribe((cart) => {
          const skus = cart.orderLines.map((orderLine) => orderLine.sku);
          skus?.length && this.store$.dispatch(fetchProductsRecommendations({ skus }));
          setTimeout(() => this.checkIsCartUpdated());
        }),
    );

    this.mxWholeSale$ = this.store$
      .select(selectCurrentConsultant)
      .pipe(map((currentConsultant) => currentConsultant?.mxWholeSale === true));

    this.isOverOrderTotalLimit$ = this.cart$.pipe(
      map((cart) => {
        const limit = this.appSettings.ecomOrderTotalLimit;
        return limit > 0 && cart.merchandiseTotal > limit;
      }),
    );

    this.hasOverLimitItems$ = this.cart$.pipe(
      map(
        (cart) =>
          cart.orderLines?.some(
            (line) => line.quantityLimit > 0 && line.quantity > line.quantityLimit,
          ),
      ),
    );

    this.subscriptions.add(
      this.isOverOrderTotalLimit$
        .pipe(
          filter((isOver) => isOver),
          distinctUntilChanged(),
        )
        .subscribe(() => {
          const limit = this.appSettings.ecomOrderTotalLimit;
          this.store$.dispatch(
            setOrderTotalLimitError({
              payload: {
                limit: limit.toString(),
                orderLineId: null,
                errorType: 'orderTotalLimit',
              },
            }),
          );
        }),
    );

    this.subscriptions.add(
      this.actions$
        .pipe(
          ofType(updateOrderLineQtySuccess),
          tap(() => (this.isRevalidating = true)),
          switchMap(() => this.cartService.validateQuantityLimits()),
        )
        .subscribe((result) => {
          if (result.allLimits?.length > 0) {
            this.store$.dispatch(patchOrderLineLimits({ limits: result.allLimits }));
          }
          this.isRevalidating = false;
        }),
    );
  }

  ngAfterViewInit(): void {
    this.checkIsCartUpdated();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onQuantityLimitModalClosed(): void {
    this.updatedItems = [];
  }

  private checkIsCartUpdated(): void {
    if (this.localStorageService.getItem(LocalStorageKey.isCartUpdated) && !this.hasOpenedModal) {
      this.itemUpdatedModal.open();
      this.hasOpenedModal = true;
    } else if (!this.hasOpenedModal) {
      this.store$.dispatch(setMultiplePurchaseLimitModalOpen({ shouldOpenModal: true }));
      this.hasOpenedModal = true;
    }
  }
}
